import { useCallback, useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { cn } from "../lib/cn";
import { Button } from "./button";

type DrawerProps = {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  actions?: ReactNode;
  className?: string;
  bodyClassName?: string;
  closeLabel?: string;
};

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function Drawer({
  open,
  title,
  onClose,
  children,
  actions,
  className,
  bodyClassName,
  closeLabel = "Close",
}: DrawerProps) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const latestOnCloseRef = useRef(onClose);

  useEffect(() => {
    latestOnCloseRef.current = onClose;
  }, [onClose]);

  const handleClose = useCallback(() => latestOnCloseRef.current(), []);

  const trapFocus = useCallback((event: KeyboardEvent) => {
    if (event.key !== "Tab") return;
    const drawer = drawerRef.current;
    if (!drawer) return;

    const focusable = Array.from(drawer.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey) {
      if (document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }
    } else if (document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  useEffect(() => {
    if (!open) return;

    previousFocusRef.current = document.activeElement as HTMLElement | null;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") handleClose();
      trapFocus(event);
    };

    window.addEventListener("keydown", onKeyDown);

    requestAnimationFrame(() => {
      const drawer = drawerRef.current;
      if (drawer) {
        const firstFocusable = drawer.querySelector<HTMLElement>(FOCUSABLE);
        if (firstFocusable) firstFocusable.focus();
        else drawer.focus();
      }
    });

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      previousFocusRef.current?.focus();
    };
  }, [open, handleClose, trapFocus]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    <div className="cf-drawer-backdrop" role="presentation" onClick={onClose}>
      <div
        ref={drawerRef}
        className={cn("cf-drawer", className)}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="cf-drawer-header">
          <h2 className="cf-drawer-title">{title}</h2>
          <div className="cf-drawer-actions">
            {actions}
            <Button type="button" variant="ghost" tone="neutral" onClick={onClose}>
              {closeLabel}
            </Button>
          </div>
        </div>
        <div className={cn("cf-drawer-body", bodyClassName)}>{children}</div>
      </div>
    </div>
  );
}
